import { useState } from 'react';
import { userExtraPhoneAdd, userExtraPhoneRemove, userExtraPhoneVerify, userExtraPhones, type ApiError } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, maskPhone, pre, sx, useApi, useGuarded } from './kit';

// My connections, Other phones (Console V5; design T10502-10530, xp values V5 L35600-35640;
// v3/settings/extra-phones.ts): up to 3 more numbers, each proved by its own 6-digit code
// (10 minutes). Extra numbers get alerts only; approvals, the PIN and Pause AI by text work
// from the main phone. The Aithora team only: the server refuses everyone else, so the
// block is drawn only for the team. The design's Work / Personal / WhatsApp label is not
// stored by the server, so it is not offered.

const INPUT = 'height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;font-size:16px;font-family:inherit;background:#FCFAF4;box-sizing:border-box;';
const BTN = 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
const STATE = 'font-size:14px;font-weight:800;letter-spacing:.06em;border-radius:5px;padding:2px 7px;white-space:nowrap;color:#12503C;background:rgba(31,107,82,.13);';

export function ExtraPhones() {
  const { client, session } = useSession();
  const { wr } = useGuarded();
  const me = session?.user.id ?? '';
  const q = useApi((c, s) => userExtraPhones(c, { params: { id: me }, signal: s }), [me], !!me);
  const [adding, setAdding] = useState(false);
  const [num, setNum] = useState('');
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState<ApiError | null>(null);
  if (!q.data) return null;
  const d = q.data;
  const verifying = !!d.pendingPhone;
  const digits = num.replace(/\D/g, '');
  const send = async () => {
    if (digits.length < 10) return;
    setErr(null);
    try {
      const r = await userExtraPhoneAdd(client, { params: { id: me }, body: { phone: '+' + (digits.length === 10 ? '1' + digits : digits) } });
      q.setData((p) => (p ? { ...p, pendingPhone: r.pendingPhone } : p));
      setAdding(false);
      setCode('');
      setMsg('We texted a 6-digit code. It expires in 10 minutes.');
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const verify = async () => {
    if (code.length !== 6) { setMsg('Enter all 6 digits.'); return; }
    setErr(null);
    try {
      await userExtraPhoneVerify(client, { params: { id: me }, body: { code } });
      setNum('');
      q.reload();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const remove = async (id: string) => {
    setErr(null);
    try {
      await userExtraPhoneRemove(client, { params: { id: me, phoneId: id } });
      q.setData((p) => (p ? { ...p, phones: p.phones.filter((x) => x.id !== id) } : p));
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  return (
    <div data-testid="extra-phones" style={sx('padding:12px 2px;border-top:1px solid rgba(34,39,46,.1);display:flex;flex-direction:column;gap:6px;')}>
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
        <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>Other phones</span>
        <span style={sx('font-size:16px;color:#54606C;')}>{d.phones.length + ' of ' + d.max + ' extra'}</span>
        <span style={sx('flex:1 1 10px;')} />
        {d.phones.length < d.max && !verifying && (
          <a href="#" onClick={(e) => { pre(e); setAdding(!adding); }} style={sx(BTN + 'border:1px solid rgba(34,39,46,.2);color:#22272E;')}>Add a phone</a>
        )}
      </div>
      {d.phones.map((x) => (
        <div key={x.id} style={sx('display:flex;align-items:flex-start;gap:10px;padding:6px 0;border-top:1px solid rgba(34,39,46,.06);')}>
          <div style={sx('flex:1 1 auto;min-width:0;')}>
            <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
              <span style={sx('font-size:16px;font-variant-numeric:tabular-nums;color:#22272E;')}>{x.phone ? maskPhone(x.phone) : '•••-' + x.last4}</span>
              <span style={sx(STATE)}>VERIFIED</span>
            </div>
            <div style={sx('font-size:16px;color:#54606C;margin-top:2px;line-height:1.4;')}>Alerts and heads-ups only. Approvals and Pause AI by text work from your main phone.</div>
          </div>
          <a href="#" {...wr(() => void remove(x.id))} style={sx('flex:0 0 auto;font-size:16px;font-weight:700;color:#B3261E;text-decoration:none;')}>Remove</a>
        </div>
      ))}
      {d.phones.length === 0 && !adding && !verifying && (
        <div style={sx('font-size:16px;color:#54606C;line-height:1.45;')}>Only your main phone. Add a work, personal or WhatsApp number to get updates there too.</div>
      )}
      {adding && !verifying && (
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
          <input type="tel" aria-label="Phone number" value={num} onChange={(e) => setNum(e.target.value)} placeholder="Phone number" style={sx(INPUT + 'width:160px;')} />
          <a href="#" {...wr(() => void send())} style={sx(BTN + (digits.length >= 10 ? 'background:#264C7E;color:#fff;' : 'background:rgba(34,39,46,.07);color:#54606C;cursor:default;'))}>Text me a code</a>
        </div>
      )}
      {verifying && (
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
          <span style={sx('font-size:16px;font-weight:600;color:#22272E;')}>{d.pendingPhone}</span>
          <input type="text" inputMode="numeric" aria-label="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit code" style={sx(INPUT + 'width:120px;')} />
          <a href="#" {...wr(() => void verify())} style={sx(BTN + 'background:#264C7E;color:#fff;')}>Verify</a>
          <span style={sx('font-size:16px;color:#54606C;')}>{msg || 'We texted a 6-digit code. It expires in 10 minutes.'}</span>
        </div>
      )}
      <ErrLine error={err} style="margin-top:0;" />
    </div>
  );
}
